import React, { useEffect, useState } from 'react';
import { Select, SelectItem, RadioGroup, Radio, Button } from '@heroui/react';
import { ILocationFilter, ReportType } from '../../../types/reportsTypes';
import { useAppDispatch, useAppSelector } from '../../../../../core/rootHooks';
import { generalSeaAreasActions } from '../../../slices/generalSeaAreasSlice';
import { selectGeneralSeaAreas, selectGeneralSeaAreasLoading } from '../../../slices/generalSeaAreasSlice';
import { TGeneralSeaAreas } from '../../../types/downloadTypes';
import { NumberRange } from '../../../../../shared/components/number-range/NumberRange';
import { detailedPolygonsActions, selectDetailedPolygons } from '../../../../main-map/slices/detailedPolygonsSlice';
import { PolygonSelectorModal } from './PolygonSelectorModal';
import { PortMultiSelector } from './PortMultiSelector';

interface LocationSelectorProps {
	value: ILocationFilter;
	onChange: (value: ILocationFilter) => void;
	reportType?: ReportType; // Optional prop to adjust available filters based on report type
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({ value, onChange, reportType }) => {
	const dispatch = useAppDispatch();
	const generalSeaAreas = useAppSelector(selectGeneralSeaAreas);
	const isLoading = useAppSelector(selectGeneralSeaAreasLoading);
	const detailedPolygons = useAppSelector(selectDetailedPolygons);

	const [isPolygonModalOpen, setIsPolygonModalOpen] = useState(false);

	useEffect(() => {
		if (!generalSeaAreas) {
			dispatch(generalSeaAreasActions.getGeneralSeaAreasRequestAction());
		}
	}, [dispatch, generalSeaAreas]);

	useEffect(() => {
		if (!detailedPolygons || !detailedPolygons.features || detailedPolygons.features.length === 0) {
			// If polygons are not loaded, dispatch the action to fetch them
			dispatch(detailedPolygonsActions.getDetailedPolygonsRequestAction());
		}
	}, [dispatch, detailedPolygons]);

	const handleTypeChange = (type: 'all' | 'latlong' | 'seaArea' | 'polygon' | 'port') => {
		// Clear values from other options when switching types
		const updatedValue: ILocationFilter = { ...value, type };

		// Clear coordinates if not lat/long
		if (type !== 'latlong') {
			updatedValue.coordinates = undefined;
		}

		// Clear areaId if not sea area
		if (type !== 'seaArea') {
			updatedValue.areaId = undefined;
		}

		// Clear polygonId if not polygon
		if (type !== 'polygon') {
			updatedValue.polygonId = undefined;
		}

		// Clear port-related fields if not port
		if (type !== 'port') {
			updatedValue.portNames = undefined;
			updatedValue.portPolygonIds = undefined;
		}

		onChange(updatedValue);

		// Auto-open polygon modal when polygon type is selected
		if (type === 'polygon') {
			setIsPolygonModalOpen(true);
		}
	};

	const handleCoordinateChange = (field: string, val: string) => {
		const numVal = val !== '' ? parseFloat(val) : null;

		onChange({
			...value,
			coordinates: {
				...value.coordinates,
				[field]: numVal,
			},
		});
	};

	return (
		<div className="w-full flex flex-row">
			<span className="text-sm font-medium text-msi-midblue-color-500 w-1/3">Location:</span>

			<div className="w-2/3 flex flex-col gap-2">
				{/* All option */}
				<div className="flex items-center">
					<RadioGroup
						value={value.type}
						onValueChange={(val) => handleTypeChange(val as 'all' | 'latlong' | 'seaArea' | 'polygon' | 'port')}
						size="sm"
						className="flex items-center"
						orientation="horizontal"
						aria-label="Location filter type"
					>
						<Radio value="all">All</Radio>
					</RadioGroup>
				</div>

				{/* Lat/Long option with accordion content */}
				<div className="flex flex-col">
					<RadioGroup
						value={value.type}
						onValueChange={(val) => handleTypeChange(val as 'all' | 'latlong' | 'seaArea' | 'polygon' | 'port')}
						size="sm"
						className="flex"
						orientation="horizontal"
						aria-label="Location filter type"
					>
						<Radio value="latlong">Lat/Long Range</Radio>
					</RadioGroup>

					<div className="ml-6 mt-2 space-y-2">
						<NumberRange
							label="Lat"
							fromValue={value.coordinates?.lat ?? null}
							toValue={value.coordinates?.latTo ?? null}
							onFromChange={(val) => handleCoordinateChange('lat', val)}
							onToChange={(val) => handleCoordinateChange('latTo', val)}
							minValue={-90}
							maxValue={90}
							fromPlaceholder="-90"
							toPlaceholder="90"
							isDisabled={value.type !== 'latlong'}
							size="sm"
						/>
						<NumberRange
							label="Long"
							fromValue={value.coordinates?.lon ?? null}
							toValue={value.coordinates?.lonTo ?? null}
							onFromChange={(val) => handleCoordinateChange('lon', val)}
							onToChange={(val) => handleCoordinateChange('lonTo', val)}
							minValue={-180}
							maxValue={180}
							fromPlaceholder="-180"
							toPlaceholder="180"
							isDisabled={value.type !== 'latlong'}
							size="sm"
						/>
					</div>
				</div>

				{/* Sea Area option with accordion content */}
				<div className="flex items-center">
					<RadioGroup
						value={value.type}
						onValueChange={(val) => handleTypeChange(val as 'all' | 'latlong' | 'seaArea' | 'polygon' | 'port')}
						size="sm"
						className="flex items-center"
						orientation="horizontal"
						aria-label="Location filter type"
					>
						<Radio value="seaArea" />
					</RadioGroup>

					<div className="w-full ml-2">
						<Select
							selectedKeys={value.areaId ? new Set(value.areaId) : new Set()}
							onSelectionChange={(keys) => {
								const selectedKeys = Array.from(keys) as string[];
								onChange({ ...value, areaId: selectedKeys.length > 0 ? selectedKeys : undefined });
							}}
							placeholder={isLoading ? 'Loading...' : 'Select Sea Area'}
							size="sm"
							isDisabled={value.type !== 'seaArea' || isLoading}
							selectionMode="single"
							className="max-w-xs"
							aria-label="Select Sea Area"
							renderValue={(items) => {
								if (items.length === 0) return '';
								if (items.length === 1) return items[0].textValue;
								return `${items.length} areas selected`;
							}}
						>
							{generalSeaAreas?.map((area: TGeneralSeaAreas) => (
								<SelectItem key={area.name.toString()}>{area.name}</SelectItem>
							)) || []}
						</Select>
					</div>
				</div>

				{/* Port option with auto-complete */}
				{(reportType === ReportType.PortCalls ||
					reportType === ReportType.StoppageEvents ||
					reportType === ReportType.Voyages) && (
					<div className="flex items-center">
						<RadioGroup
							value={value.type}
							onValueChange={(val) => handleTypeChange(val as 'all' | 'latlong' | 'seaArea' | 'polygon' | 'port')}
							size="sm"
							className="flex items-center"
							orientation="horizontal"
							aria-label="Location filter type"
						>
							<Radio value="port" />
						</RadioGroup>

						<div className="w-full ml-2">
							<PortMultiSelector
								detailedPolygons={detailedPolygons}
								selectedPortNames={value.portNames || []}
								onSelectionChange={(portNames, polygonIds) => {
									onChange({
										...value,
										portNames: portNames.length > 0 ? portNames : undefined,
										portPolygonIds: polygonIds.length > 0 ? polygonIds : undefined,
									});
								}}
								isDisabled={value.type !== 'port'}
								placeholder="Select Ports"
								size="sm"
							/>
						</div>
					</div>
				)}

				{/* Polygon option with accordion content */}
				{(reportType === ReportType.PortCalls ||
					reportType === ReportType.StoppageEvents ||
					reportType === ReportType.Voyages) && (
					<div className="flex items-center">
						<RadioGroup
							value={value.type}
							onValueChange={(val) => handleTypeChange(val as 'all' | 'latlong' | 'seaArea' | 'polygon' | 'port')}
							size="sm"
							className="flex items-center"
							orientation="horizontal"
							aria-label="Location filter type"
						>
							<Radio value="polygon" />
						</RadioGroup>

						<div className="w-full ml-2 flex flex-col gap-2">
							<Button
								size="sm"
								variant="bordered"
								onPress={() => setIsPolygonModalOpen(true)}
								isDisabled={value.type !== 'polygon'}
								className="justify-start"
								aria-label={
									value.polygonId && value.polygonId.length > 0
										? `${value.polygonId.length} polygon${value.polygonId.length !== 1 ? 's' : ''} selected. Click to modify selection`
										: 'Select Polygons'
								}
							>
								{value.polygonId && value.polygonId.length > 0
									? `${value.polygonId.length} polygon${value.polygonId.length !== 1 ? 's' : ''} selected`
									: 'Select Polygons'}
							</Button>

							{/* {value.polygonId && value.polygonId.length > 0 && (
                            <div className="text-xs text-gray-600 max-h-16 overflow-y-auto p-2 bg-gray-50 rounded">
                                <strong>Selected:</strong> {value.polygonId.join(', ')}
                            </div>
                        )} */}
						</div>
					</div>
				)}
			</div>

			<PolygonSelectorModal
				isOpen={isPolygonModalOpen}
				onClose={() => setIsPolygonModalOpen(false)}
				onConfirm={(selectedIds) => {
					onChange({ ...value, polygonId: selectedIds.length > 0 ? selectedIds : undefined });
				}}
				detailedPolygons={detailedPolygons}
				selectedPolygonIds={value.polygonId || []}
			/>
		</div>
	);
};
